import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { Subject, firstValueFrom } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

import { DashboardApiService } from 'src/app/api/services/dashboard-api.service';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';

import {
  share as shareAction,
  shareCancelled,
  shareFailure,
  shareSuccess,
  updateObjectViewProperties,
} from '../state/object.actions';
import {
  togglePeriodLocked as togglePeriodLockedAction,
  togglePeriodLockedSuccess,
} from '../state/core.actions';
import { submitFormFailure, submitFormSuccess } from '../state/form.actions';
import { NomExportTransportTypes, NomObjectClass } from '../constants';
import { EditorFormEntity } from '../models/form.models';
import { ContentFilters, Period } from '../models/core.models';
import { selectCurrentObject } from '../state';
import { ShareSettings } from '../models/object.models';
import { WidgetViewProperties } from 'src/app/api/services/feed-api.service';

export interface ShareInput {
  exportTransportId: NomExportTransportTypes;
  objectId: string;
  objectClassId: number;
  postIds: string[];
  contentFilters: ContentFilters;
  settings: ShareSettings;
  shareParams?: any;
}

/**
 * Object-scoped writes whose payload is assembled from the **current object** in
 * the store, plus the share flow with its per-object cancellation.
 *
 * A service rather than mutation hooks because the payloads need store state and
 * the share/cancel pair spans two components. Each method is its write path's
 * single owner: HTTP first, then any `…Success` broadcast the client state needs.
 */
@Injectable({ providedIn: 'root' })
export class ObjectPersistenceService {
  private readonly store = inject(Store);
  private readonly utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private readonly dashboardApi = inject(DashboardApiService);

  /** Emits the objectId whose in-flight share the user cancelled. */
  private readonly shareCancel$ = new Subject<string>();

  /**
   * Persist the period lock for the current object. The trigger action still
   * broadcasts first - the object reducer adopts the locked period as the new
   * default, and `togglePeriodLockedSuccess` drives the URL param sync effect.
   */
  async togglePeriodLocked(
    isPeriodLocked: boolean,
    period: Period,
  ): Promise<void> {
    this.store.dispatch(togglePeriodLockedAction({ isPeriodLocked, period }));

    const currentObject = await firstValueFrom(
      this.store.select(selectCurrentObject).pipe(take(1)),
    );

    try {
      await firstValueFrom(
        this.utilsDataObjectApi.togglePeriodLocked(
          currentObject.id,
          currentObject.object_class_id,
          isPeriodLocked,
          period,
        ),
      );

      this.store.dispatch(togglePeriodLockedSuccess({ isPeriodLocked }));
    } catch (error) {
      // parity with the retired effect: the failure was never surfaced
      console.error('Failed to persist period lock', error);
    }
  }

  /**
   * Update the current object's widget view properties. The action is the client
   * state - both the object and feed reducers apply it optimistically. Only
   * dashboards persist here; a feed's copy rides along in the feed-settings
   * autosave.
   */
  async updateViewProperties(
    viewProperties: WidgetViewProperties,
  ): Promise<void> {
    this.store.dispatch(updateObjectViewProperties({ viewProperties }));

    const currentObject = await firstValueFrom(
      this.store.select(selectCurrentObject).pipe(take(1)),
    );

    if (currentObject?.object_class_id !== NomObjectClass.DASHBOARD) {
      return;
    }

    try {
      await firstValueFrom(
        this.dashboardApi.saveDashboardSettings(
          currentObject.id,
          viewProperties,
        ),
      );
    } catch (error) {
      console.error('Failed to persist dashboard view properties', error);
    }
  }

  /**
   * One-off export. The trigger action raises `isExporting` for FILE exports;
   * the success/failure pair is double-broadcast into the export form flow
   * (`submitForm…` with `EditorFormEntity.ExportForm`) exactly as the retired
   * `share$` effect did. `cancelShare` aborts the in-flight HTTP request.
   */
  share(input: ShareInput): void {
    const {
      exportTransportId,
      objectId,
      objectClassId,
      postIds,
      contentFilters,
      settings,
      shareParams = {},
    } = input;

    this.store.dispatch(
      shareAction({
        exportTransportId,
        objectId,
        objectClassId,
        postIds,
        contentFilters,
        settings,
        shareParams,
      }),
    );

    this.utilsDataObjectApi
      .share(
        objectId,
        objectClassId,
        postIds,
        contentFilters,
        settings,
        shareParams,
      )
      .pipe(
        takeUntil(
          this.shareCancel$.pipe(
            filter((cancelledId) => cancelledId === objectId),
            tap(() => this.store.dispatch(shareCancelled({ objectId }))),
          ),
        ),
      )
      .subscribe({
        next: (response) => {
          this.store.dispatch(shareSuccess({ response }));
          this.store.dispatch(
            submitFormSuccess({
              entity: EditorFormEntity.ExportForm,
              object: response,
              isCreated: false,
            }),
          );
        },
        error: (error) => {
          this.store.dispatch(shareFailure({ error }));
          this.store.dispatch(
            submitFormFailure({ entity: EditorFormEntity.ExportForm, error }),
          );
        },
      });
  }

  /** No-op when no share is in flight for the object - same as the old effect. */
  cancelShare(objectId: string): void {
    this.shareCancel$.next(objectId);
  }
}
